"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import styles from "./ViewUserAdditionModal.module.css";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiPlus,
  FiSave,
  FiUpload,
  FiX,
  FiChevronDown,
  FiRotateCcw,
} from "react-icons/fi";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import CreateContextModal from "Components/FeatureFlip/Common/Context/CreateContextModal";
import { useParams } from "react-router-dom";
import { useGetAllTreatmentValues } from "API/Queries/useGetAllTreatmentValues";
import { useAddTreatmentsValue } from "API/Queries/useAddTreatmentsValue";
import { useDeleteTreatmentsValue } from "API/Queries/useDeleteTreatmentsValue";

interface IViewUserAdditionModalProps {
  selectedTreatmentName: string;
  setShowUserModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTreatmentId: string;
}

interface SelectedContext {
  id: string;
  name: string;
}

interface Context {
  id: string;
  name: string;
}

const ViewUseAdditionModal = (props: IViewUserAdditionModalProps) => {
  const { selectedTreatmentName, setShowUserModal, selectedTreatmentId } =
    props;

  const [inputValue, setInputValue] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id: projectId, featureId } = useParams();

  const [savedValues, setSavedValues] = useState<string[]>([]);
  const [removedValues, setRemovedValues] = useState<string[]>([]);
  const [newValues, setNewValues] = useState<string[]>([]);
  const [duplicateValues, setDuplicateValues] = useState<string[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [selectedContext, setSelectedContext] =
    useState<SelectedContext | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCreateContext, setShowCreateContext] = useState(false);

  const contexts = useFeatureFlipStore((s) => s.contexts);
  const { data: treatmentValue } =
    useGetAllTreatmentValues(selectedTreatmentId);
  const { mutateAsync: addTreatmentValue } = useAddTreatmentsValue();
  const { mutateAsync: deleteTreatmentValue } = useDeleteTreatmentsValue();
  const userListRef = useRef<HTMLDivElement>(null);

  // Extract complex logic
  const contextValue = [
    ...savedValues.filter((v) => !removedValues.includes(v)),
    ...newValues,
  ];
  const hasContexts = contexts && contexts.length > 0;
  const isContextLocked = savedValues.length > 0;
  const canSaveChanges = hasUnsavedChanges && selectedContext;
  const shouldShowConfirmModal = hasUnsavedChanges;
  const shouldShowCreateContextButton = !hasContexts;
  const shouldShowContextNudge = hasContexts && !selectedContext;

  // Extract event handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleDropdownToggle = () => {
    if (!isContextLocked) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleContextSelect = (context: { id: string; name: string }) => () => {
    setSelectedContext({ id: context.id, name: context.name });
    setIsDropdownOpen(false);
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleCreateContextClick = () => {
    setShowCreateContext(true);
  };

  const handleCreateContextClose = () => {
    setShowCreateContext(false);
  };

  const handleCreateContextSuccess = (newContext?: Context) => {
    setShowCreateContext(false);
    if (newContext) {
      setSelectedContext({ id: newContext.id, name: newContext.name });
    }
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
  };

  const handleDiscardChanges = () => {
    setShowUserModal(false);
  };

  const addUsers = (ids: string[]) => {
    const allCurrentValues = [...savedValues, ...newValues];

    const removedToRestore = ids.filter((id) => removedValues.includes(id));
    const remainingIds = ids.filter((id) => !removedValues.includes(id));

    if (removedToRestore.length) {
      setRemovedValues((prev) =>
        prev.filter((id) => !removedToRestore.includes(id))
      );
    }

    const duplicates = remainingIds.filter((id) =>
      allCurrentValues.includes(id)
    );
    const unique = remainingIds.filter((id) => !allCurrentValues.includes(id));

    if (duplicates.length) {
      setDuplicateValues(duplicates);
      setTimeout(() => setDuplicateValues([]), 3000);
    }

    if (unique.length) {
      setNewValues((prev) => [...prev, ...unique]);
    }

    const hasNewChanges = removedToRestore.length || unique.length;
    if (hasNewChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const handleAddUsers = (): void => {
    if (!inputValue.trim()) return;

    const newIds = inputValue
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
    addUsers(newIds);

    setInputValue("");
    if (userListRef.current) {
      userListRef.current.scrollTop = userListRef.current.scrollHeight;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddUsers();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idsFromFile = result
        .split(/[\n,]/)
        .map((id) => id.trim())
        .filter(Boolean);

      addUsers(idsFromFile);
      e.target.value = "";
      if (userListRef.current) {
        userListRef.current.scrollTop = userListRef.current.scrollHeight;
      }
    };
    reader.readAsText(file);
  };

  const handleRemoveUser = (id: string) => {
    const isSavedValue = savedValues.includes(id);
    const isNewValue = newValues.includes(id);

    if (isSavedValue) {
      setRemovedValues((prev) => [...prev, id]);
    } else if (isNewValue) {
      setNewValues((prev) => prev.filter((v) => v !== id));
    }
    setHasUnsavedChanges(true);
  };

  const handleRemoveUserClick = (id: string) => () => {
    handleRemoveUser(id);
  };

  const handleModalClose = () => {
    if (shouldShowConfirmModal) {
      setShowConfirmModal(true);
    } else {
      setShowUserModal(false);
    }
  };

  const handleSaveUsers = async () => {
    const hasNewValues = newValues.length && selectedContext?.id;
    const hasRemovedValues = removedValues.length && selectedContext?.id;

    if (hasNewValues) {
      await addTreatmentValue({
        projectId: projectId as string,
        treatmentId: selectedTreatmentId,
        contextId: selectedContext?.id,
        contextValues: newValues,
        featureId: featureId as string,
      });
    }

    if (hasRemovedValues) {
      await deleteTreatmentValue({
        projectId: projectId as string,
        treatmentId: selectedTreatmentId,
        contextId: selectedContext?.id,
        contextValues: removedValues,
        featureId: featureId as string,
      });
    }

    setShowUserModal(false);
  };

  const handleRestoreValue = (id: string) => {
    setRemovedValues((prev) => prev.filter((v) => v !== id));
    setHasUnsavedChanges(true);
  };

  const handleRestoreValueClick = (id: string) => () => {
    handleRestoreValue(id);
  };

  useEffect(() => {
    const hasChanges = removedValues.length > 0 || newValues.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [removedValues, newValues]);

  useEffect(() => {
    const hasTreatmentValue = treatmentValue?.contextId && contexts?.length;
    if (hasTreatmentValue) {
      const storedContext = contexts.find(
        (c) => c.id === treatmentValue.contextId
      );
      if (storedContext) {
        setSelectedContext({ id: storedContext.id, name: storedContext.name });
      }
    }

    const hasContextValues = !!treatmentValue?.contextValues?.length;
    if (hasContextValues) {
      setSavedValues(treatmentValue.contextValues);
    }

    if (userListRef.current) {
      userListRef.current.scrollTop = userListRef.current.scrollHeight;
    }
  }, [contexts, treatmentValue]);

  const getPlaceholderText = () => {
    return `Add ${selectedContext?.name} (comma-separated)`;
  };

  const getUploadHintText = () => {
    return ` or upload a CSV/TXT file with ${selectedContext?.name} IDs`;
  };

  const getDuplicateErrorMessage = () => {
    if (duplicateValues.length === 1) {
      return `${selectedContext?.name} "${duplicateValues[0]}" already exists`;
    }
    return `${selectedContext?.name} ${duplicateValues
      .map((id) => `"${id}"`)
      .join(", ")} already exist`;
  };

  const getRemovedItemsText = () => {
    const count = removedValues.length;
    return `${count} ${count === 1 ? "item" : "items"}`;
  };

  // Render confirmation modal
  if (showConfirmModal) {
    return (
      <div className={styles.confirmModal}>
        <div className={styles.confirmContent}>
          <div className={styles.confirmHeader}>
            <FiAlertTriangle className={styles.warningIcon} />
            <h2>Unsaved Changes</h2>
          </div>
          <p className={styles.confirmMessage}>
            You have unsaved changes in your segment. Are you sure you want to
            close without saving?
          </p>
          <div className={styles.confirmActions}>
            <button
              className={styles.cancelButton}
              onClick={handleConfirmModalClose}
            >
              Keep Editing
            </button>
            <button
              className={styles.discardButton}
              onClick={handleDiscardChanges}
            >
              Discard Changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <h2>Manage Users - {selectedTreatmentName}</h2>
            {hasContexts && (
              <div className={styles.contextDropdown}>
                <button
                  className={`${styles.dropdownButton} ${
                    isContextLocked ? styles.locked : ""
                  }`}
                  onClick={handleDropdownToggle}
                  disabled={isContextLocked}
                >
                  {selectedContext?.name || "Select Context"}
                  {!isContextLocked && (
                    <FiChevronDown
                      className={isDropdownOpen ? styles.rotated : ""}
                    />
                  )}
                </button>
                {isDropdownOpen && !isContextLocked && (
                  <div className={styles.dropdownMenu}>
                    {contexts?.map((context) => (
                      <button
                        key={context.id}
                        className={styles.dropdownItem}
                        onClick={handleContextSelect(context)}
                      >
                        {context.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button className={styles.closeButton} onClick={handleModalClose}>
            <FiX />
          </button>
        </div>

        <div className={styles.modalBody}>
          {shouldShowCreateContextButton ? (
            <div className={styles.emptyContext}>
              <FiAlertCircle size={24} />
              <div className={styles.emptyContextContent}>
                <h3>No Contexts Available</h3>
                <p>
                  You need to create a context before adding users to segments.
                </p>
                <button
                  className={styles.createContextButton}
                  onClick={handleCreateContextClick}
                >
                  <FiPlus /> Create Context
                </button>
              </div>
            </div>
          ) : shouldShowContextNudge ? (
            <div className={styles.contextNudge}>
              <FiAlertCircle />
              Please select a context to start adding users
            </div>
          ) : (
            <>
              <div className={styles.inputSection}>
                <div className={styles.userInput}>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder={getPlaceholderText()}
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    className={styles.addUserButton}
                    onClick={handleAddUsers}
                    disabled={!inputValue.trim()}
                  >
                    <FiPlus /> Add
                  </button>
                </div>

                <div className={styles.uploadSection}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                  />
                  <button
                    className={styles.uploadButton}
                    onClick={handleFileInputClick}
                  >
                    <FiUpload /> Upload CSV
                  </button>
                  <span className={styles.uploadHint}>
                    {getUploadHintText()}
                  </span>
                </div>
              </div>

              {!!duplicateValues.length && (
                <div className={styles.errorNudge}>
                  <FiAlertCircle />
                  {getDuplicateErrorMessage()}
                </div>
              )}

              <div ref={userListRef} className={styles.userList}>
                <div className={styles.userTags}>
                  {contextValue.map((userId) => {
                    const isNewValue = newValues.includes(userId);
                    const tagClassName = `${styles.tag} ${
                      isNewValue ? styles.newTag : styles.savedTag
                    }`;

                    return (
                      <span key={userId} className={tagClassName}>
                        {userId}
                        <button
                          onClick={handleRemoveUserClick(userId)}
                          className={styles.removeTag}
                        >
                          <FiX />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>

              {removedValues.length > 0 && (
                <div className={styles.removedSection}>
                  <div className={styles.removedHeader}>
                    <h4>Removed {selectedContext?.name}</h4>
                    <span className={styles.removedCount}>
                      {getRemovedItemsText()}
                    </span>
                  </div>
                  <div className={styles.removedTags}>
                    {removedValues.map((userId) => (
                      <span
                        key={userId}
                        className={`${styles.tag} ${styles.removedTag}`}
                      >
                        {userId}
                        <button
                          onClick={handleRestoreValueClick(userId)}
                          className={styles.restoreTag}
                          title="Restore"
                        >
                          <FiRotateCcw />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.saveButton}
            onClick={handleSaveUsers}
            disabled={!canSaveChanges}
          >
            <FiSave /> Save Changes
          </button>
        </div>
      </div>

      {showCreateContext && (
        <CreateContextModal
          isOpen={showCreateContext}
          onClose={handleCreateContextClose}
          onSuccess={handleCreateContextSuccess}
        />
      )}
    </div>
  );
};

export default ViewUseAdditionModal;
